import { useEffect, useRef, useState } from "react";

/** Clean axis ticks: 0, 250, 500… — steps of 1, 2, 2.5 or 5 × 10ⁿ. */
export function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const rough = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= rough)!;
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => Number((i * step).toFixed(10)));
}

/** Width of an element, tracked with ResizeObserver — charts draw at real pixel size. */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry!.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Pick ~n evenly spaced indexes (always including the last) for x-axis labels. */
export function labelIndexes(length: number, n: number): Set<number> {
  if (length <= n) return new Set(Array.from({ length }, (_, i) => i));
  const step = Math.ceil(length / n);
  const out = new Set<number>();
  for (let i = length - 1; i >= 0; i -= step) out.add(i);
  return out;
}
